import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, UserCircle, Zap, MessageCircle, ExternalLink, MoreHorizontal, Flag, Ban, Bookmark, Share2, UserPlus, UserCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import type { FeedComment } from '../../types/index'; // Assuming FeedComment is the base type

// Re-defining PopularComment here for now, can be moved to types/index.ts later
export interface PopularComment extends FeedComment {
  type: 'comment'; // Add type discriminator for comments

  engagement?: number;
  replies?: { length: number };
  likes?: number;
  // Add any other properties specific to popular comments if needed
}

interface AdPlaceholder {
  type: 'ad';
  id: string;
  content: string; // Placeholder for ad content or component
}

type SliderItem = PopularComment | AdPlaceholder;

interface CommentSliderProps {
  comments: PopularComment[];
}

const CommentSlider: React.FC<CommentSliderProps> = ({ comments }) => {
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const sliderItems = useMemo(() => {
    if (!comments || comments.length === 0) {
      return [];
    }
    const items: SliderItem[] = comments.map(c => ({ ...c, type: 'comment' as const })); // Ensure comments have type:'comment'

    if (comments.length >= 1) { // Ad after 1st comment, means we need at least 1 comment to show ad after it.
                               // If user meant strictly between 1st and 2nd, then comments.length >= 2
      const adItem: AdPlaceholder = {
        type: 'ad',
        id: 'ad-placeholder-1',
        content: 'Discover new music from emerging artists on MoZo Premium',
      };
      // Insert ad after the first comment (at index 1)
      items.splice(1, 0, adItem);
    }
    return items;
  }, [comments]);

  if (sliderItems.length === 0) {
    return null;
  }

  const showPrevItem = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentItemIndex((prevIndex) =>
      prevIndex === 0 ? sliderItems.length - 1 : prevIndex - 1
    );
  };

  const showNextItem = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentItemIndex((prevIndex) =>
      prevIndex === sliderItems.length - 1 ? 0 : prevIndex + 1
    );
  };

  const currentItem = sliderItems[currentItemIndex];

  return (
    <div className="mt-2 px-4">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Popular Content
        </h3>
        {sliderItems.length > 1 && (
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => showPrevItem(e)}
              className="p-1 rounded-md hover:bg-[#065900] active:bg-[#054700] transition-colors duration-200 group"
              aria-label="Previous item"
            >
              <ChevronLeft size={18} className="text-gray-600 dark:text-gray-400 group-hover:text-white group-active:text-white" />
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {currentItemIndex + 1} / {sliderItems.length}
            </span>
            <button
              onClick={(e) => showNextItem(e)}
              className="p-1 rounded-md hover:bg-[#065900] active:bg-[#054700] transition-colors duration-200 group"
              aria-label="Next item"
            >
              <ChevronRight size={18} className="text-gray-600 dark:text-gray-400 group-hover:text-white group-active:text-white" />
            </button>
          </div>
        )}
      </div>
      {currentItem && (
        <div className="text-xs py-2 min-h-[8em] flex flex-col justify-start items-start w-full">
          {currentItem.type === 'comment' ? (
            <div className="w-full p-2 text-left">
              {/* Line 1: Avatar and Username */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                  {currentItem.user?.avatar_url ? (
                    <img src={currentItem.user.avatar_url} alt={currentItem.user.username || 'User avatar'} className="h-6 w-6 rounded-full object-cover" />
                  ) : (
                    <UserCircle size={24} className="text-gray-400 dark:text-gray-500" />
                  )}
                  <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                    {currentItem.user?.username || 'Unknown User'}
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  {/* Show UserPlus if not following, UserCheck if following */}
                  {currentItem.user?.is_followed ? (
                    <UserCheck size={16} className="text-[#ffc11f]" />
                  ) : (
                    <UserPlus size={16} className="text-white" />
                  )}
                </div>
              </div>

              {/* Line 2: Comment Text */}
              <p className="text-sm text-gray-700 dark:text-gray-300 my-1 break-words ml-8"> {/* Indent to align with username */}
                {currentItem.content}
              </p>

              {/* Line 3: Actions */}
              <div className="flex items-center mt-2 text-gray-500 dark:text-gray-400 ml-8"> {/* Indent to align with username */}
                <div className="flex items-center space-x-3">
                  <button 
                    className="hover:text-[#ffc11f] focus:outline-none flex items-center gap-1" 
                    aria-label="Like comment"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Toggle like functionality would go here
                    }}
                  >
                    <Zap size={16} className="hover:fill-[#ffc11f]" />
                    <span className="text-xs">{currentItem.likes || 0}</span>
                  </button>
                  <button className="hover:text-blue-500 focus:outline-none" aria-label="Reply to comment">
                    <MessageCircle size={16} />
                  </button>
                  <span className="text-xs">
                    {`${currentItem.replies?.length || 0} ${((currentItem.replies?.length || 0) === 1) ? 'reply' : 'replies'}`}
                  </span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button 
                        className="p-1 rounded-md hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white transition-colors duration-200 focus:outline-none ml-1" 
                        aria-label="More options"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal size={16} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent 
                      className="w-48 p-0" 
                      align="end" 
                      side="bottom" 
                      sideOffset={5}
                      avoidCollisions
                      collisionPadding={10}
                    >
                      <div className="flex flex-col py-1">
                        <button className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white transition-colors duration-200">
                          <Share2 size={14} />
                          <span>Share</span>
                        </button>
                        <button className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white transition-colors duration-200">
                          <Bookmark size={14} />
                          <span>Save</span>
                        </button>
                        <button className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white transition-colors duration-200">
                          <Flag size={14} />
                          <span>Report</span>
                        </button>
                        <button className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white transition-colors duration-200">
                          <Ban size={14} />
                          <span>Not interested</span>
                        </button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full p-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-100 dark:border-blue-800 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="font-medium text-sm text-blue-600 dark:text-blue-400">Sponsored</span>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{currentItem.content}</p>
                </div>
                <button className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline">
                  <span>Learn more</span>
                  <ExternalLink size={12} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentSlider;
